import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import * as React from "react";

import type { FileRecord } from "@/shared/domain/files";
import {
  MAX_MATCH_PATHS,
  type IngestionRun,
} from "@/shared/domain/ingestionRules";
import { useLatestIngestionRuns } from "@/shared/domain/ingestionRules/hooks";

import {
  firstRunDeadlines,
  LatestRunsContext,
  mayHaveRun,
  useIsOrgMember,
  type RunLookup,
} from "./latestIngestionRuns";

/**
 * The latest ingestion runs of the ingestable, not yet ingested files among
 * `files` (the first 200), for every `IngestionLabelBadge` below it: one
 * request for a table's page, rather than one per badge. Asks nothing when the
 * viewer is not a member of the files' org. Asks again while a run is running,
 * or while a file uploaded within `FIRST_RUN_GRACE_MS` has no run yet.
 *
 * When a file this provider saw with a running run, or with no run, then shows
 * a run that is not running, `files` may predate that run's end. The provider
 * refetches `recordsQueryKey`, and until `recordsFetchedAt` passes the end,
 * each such file's badge shows it as Ingesting rather than reading the run's
 * outcome against the old record.
 */
export function LatestIngestionRunsProvider({
  files,
  recordsFetchedAt,
  recordsQueryKey,
  orgId,
  children,
}: React.PropsWithChildren<{
  files: readonly Pick<
    FileRecord,
    "file_id" | "ingestion_status" | "ingestable" | "created"
  >[];
  /**
   * When `files` were read from the server, as a query's `dataUpdatedAt`.
   * Undefined while they are an earlier result standing in for one still
   * loading.
   */
  recordsFetchedAt: number | undefined;
  /** The query `files` came from. */
  recordsQueryKey: QueryKey;
  /** The files' org; the current org when omitted. */
  orgId?: string;
}>) {
  const isMember = useIsOrgMember(orgId);
  const { fileIds, expectRunBy } = React.useMemo(() => {
    const lookedUp = files.filter(mayHaveRun).slice(0, MAX_MATCH_PATHS);
    return {
      fileIds: lookedUp.map((file) => file.file_id),
      expectRunBy: firstRunDeadlines(lookedUp),
    };
  }, [files]);
  const runs = useLatestIngestionRuns(fileIds, {
    enabled: isMember,
    orgId,
    expectRunBy,
  });
  const seen = useSeenRuns(runs.data, runs.dataUpdatedAt, fileIds);

  const staleIds = React.useMemo(
    () =>
      new Set(
        fileIds.filter((fileId) => {
          const end = seen.endedAt.get(fileId);
          return (
            end !== undefined &&
            (recordsFetchedAt === undefined || recordsFetchedAt <= end)
          );
        }),
      ),
    [fileIds, seen.endedAt, recordsFetchedAt],
  );
  useRefetchStaleRecords(
    staleIds,
    recordsFetchedAt !== undefined,
    recordsQueryKey,
  );

  const lookup = React.useCallback<RunLookup>(
    (fileId) => {
      if (!fileIds.includes(fileId) || !seen.answeredIds.has(fileId)) {
        return undefined;
      }
      return {
        run: seen.runs[fileId] ?? null,
        recordPredatesRunEnd: staleIds.has(fileId),
      };
    },
    [fileIds, seen, staleIds],
  );
  return (
    <LatestRunsContext.Provider value={lookup}>
      {children}
    </LatestRunsContext.Provider>
  );
}

interface SeenRuns {
  /**
   * The last runs response and the files it was asked about. A file still
   * looked up keeps its run from here while a request for a changed set of
   * files is out, so its badge does not fall back to its record meanwhile.
   */
  runs: Record<string, IngestionRun>;
  answeredIds: ReadonlySet<string>;
  /**
   * The `dataUpdatedAt` of the newest response folded in. An older response,
   * read from the cache, updates `runs` and nothing below.
   */
  observedAt: number;
  /** Files whose run was running in the newest response that included them. */
  running: ReadonlySet<string>;
  /** Files the newest response that included them showed with no run. */
  withoutRun: ReadonlySet<string>;
  /**
   * When a response first showed a file in `running` or `withoutRun` with a
   * run that is not running.
   */
  endedAt: ReadonlyMap<string, number>;
}

const NOTHING_SEEN: SeenRuns = {
  runs: {},
  answeredIds: new Set(),
  observedAt: 0,
  running: new Set(),
  withoutRun: new Set(),
  endedAt: new Map(),
};

/**
 * Folds each runs response into what this client has seen. A file missing
 * from a response, because it is no longer looked up, keeps its place in
 * `running` or `withoutRun`: paging away from it does not count as its run
 * ending, and paging back after it ended still does.
 */
function useSeenRuns(
  runs: Record<string, IngestionRun> | undefined,
  fetchedAt: number,
  fileIds: readonly string[],
): SeenRuns {
  const [seen, setSeen] = React.useState(NOTHING_SEEN);
  if (runs === undefined || runs === seen.runs) {
    return seen;
  }
  const answered = { runs, answeredIds: new Set(fileIds) };
  let next: SeenRuns = { ...seen, ...answered };
  if (fetchedAt > seen.observedAt) {
    const running = new Set(seen.running);
    const withoutRun = new Set(seen.withoutRun);
    const endedAt = new Map(seen.endedAt);
    for (const fileId of fileIds) {
      const run = runs[fileId];
      if (run === undefined) {
        withoutRun.add(fileId);
        continue;
      }
      const wasWithoutRun = withoutRun.delete(fileId);
      if (run.status === "running") {
        running.add(fileId);
        endedAt.delete(fileId);
      } else if (running.delete(fileId) || wasWithoutRun) {
        endedAt.set(fileId, fetchedAt);
      }
    }
    next = {
      ...answered,
      observedAt: fetchedAt,
      running,
      withoutRun,
      endedAt,
    };
  }
  // Set during render rather than in an effect, so no badge paints a run's
  // end against its old record first.
  setSeen(next);
  return next;
}

/**
 * Refetches the files' query once for each new set of stale files. Waits
 * while the files are a stand-in for a query still loading, whose answer
 * may already be fresh.
 */
function useRefetchStaleRecords(
  staleIds: ReadonlySet<string>,
  canRefetch: boolean,
  recordsQueryKey: QueryKey,
) {
  const queryClient = useQueryClient();
  // Callers build the key afresh on each render; the effect below must fire
  // on a change in which files are stale, not on a new array.
  const keyRef = React.useRef(recordsQueryKey);
  React.useLayoutEffect(() => {
    keyRef.current = recordsQueryKey;
  });
  const staleKey = [...staleIds].sort().join(" ");
  React.useEffect(() => {
    if (staleKey !== "" && canRefetch) {
      void queryClient.invalidateQueries({ queryKey: keyRef.current });
    }
  }, [staleKey, canRefetch, queryClient]);
}

/**
 * Looks up a file's latest run and reports it, for a badge with no provider
 * above it. Rendered only when needed, so a badge that never opens never asks.
 */
export function IngestionRunLookup({
  file,
  onRun,
}: {
  file: Pick<FileRecord, "org_id"> &
    Partial<Pick<FileRecord, "file_id" | "created">>;
  onRun: (run: IngestionRun | null | undefined) => void;
}) {
  const fileId = file.file_id;
  const isMember = useIsOrgMember(file.org_id);
  const expectRunBy = React.useMemo(
    () =>
      fileId === undefined
        ? {}
        : firstRunDeadlines([{ file_id: fileId, created: file.created }]),
    [fileId, file.created],
  );
  const runs = useLatestIngestionRuns(fileId === undefined ? [] : [fileId], {
    enabled: isMember,
    orgId: file.org_id,
    expectRunBy,
  });
  const run =
    runs.data === undefined || fileId === undefined
      ? undefined
      : runs.data[fileId] ?? null;
  React.useEffect(() => onRun(run), [run, onRun]);
  return null;
}
